//sepori-services/packages/shared/src/math.ts

import Decimal from "decimal.js";

/**
 * Price(token0/token1) from sqrtPriceX96 with decimals adjustment.
 * price = (sqrt^2 / Q96^2) * 10^(dec0 - dec1)
 */
export function price0Over1FromSqrtPriceX96(
  sqrtPriceX96: bigint,
  token0Decimals: number,
  token1Decimals: number,
  poolName = ""
): Decimal {
  const sqrt = new Decimal(sqrtPriceX96.toString());
  const Q96 = new Decimal(2).pow(96);
  const ratio = sqrt.pow(2);
  const decimalAdj = new Decimal(10).pow(token0Decimals - token1Decimals);
  let price = ratio.mul(decimalAdj).div(Q96.pow(2));

  // If you want BTC/USDC special-case inversion as in your file, uncomment:
  if (poolName.includes("cbBTC") && poolName.includes("USDC") && price.lessThan(1)) {
    price = new Decimal(1).div(price);
  }

  return price;
}

export function normalizePriceBaseOverQuote(
  baseSymbol: string,
  quoteSymbol: string,
  token0Symbol: string,
  token1Symbol: string,
  price0over1: Decimal
): Decimal {
  const b = baseSymbol.toUpperCase();
  const q = quoteSymbol.toUpperCase();
  const t0 = token0Symbol.toUpperCase();
  const t1 = token1Symbol.toUpperCase();

  if (b === t0 && q === t1) return price0over1;
  if (b === t1 && q === t0) return new Decimal(1).div(price0over1);

  // Fallback if symbols don’t match as expected.
  return price0over1;
}

export function toApiNumber(d: Decimal, sig = 15): number {
  return Number(d.toSignificantDigits(sig));
}

/**
 * Calculate USD value of a Uniswap V3 / Aerodrome Slipstream NFT position
 * Based on liquidity and current pool price
 * 
 * Formula:
 * - If current price is within range [priceLower, priceUpper]:
 *   amount0 = liquidity * (sqrt(price) - sqrt(priceLower)) / (sqrt(price) * sqrt(priceLower))
 *   amount1 = liquidity * (sqrt(priceUpper) - sqrt(price))
 * - If current price < priceLower: position is 100% token0
 * - If current price > priceUpper: position is 100% token1
 * 
 * Then convert amounts to USD using token prices
 */
export function calculateNftPositionValueUsd(params: {
  liquidity: string;           // From NFT position
  tickLower: number;          // From NFT position  
  tickUpper: number;          // From NFT position
  currentTick: number;        // From pool sugar data
  token0Decimals: number;     // From token metadata
  token1Decimals: number;     // From token metadata  
  token0PriceUsd: number;     // From market-data service
  token1PriceUsd: number;     // From market-data service
}): { valueUsd: number; amount0: number; amount1: number } {
  const {
    liquidity,
    tickLower,
    tickUpper,
    currentTick,
    token0Decimals,
    token1Decimals,
    token0PriceUsd,
    token1PriceUsd
  } = params;

  // Handle edge cases
  if (!liquidity || liquidity === '0' || liquidity === '') {
    return { valueUsd: 0, amount0: 0, amount1: 0 };
  }

  if (token0PriceUsd <= 0 || token1PriceUsd <= 0) {
    return { valueUsd: 0, amount0: 0, amount1: 0 };
  }

  try {
    // Convert ticks to prices using price = 1.0001^tick
    const tickToPrice = (tick: number): Decimal => {
      return new Decimal(1.0001).pow(tick);
    };

    const priceLower = tickToPrice(tickLower);
    const priceUpper = tickToPrice(tickUpper);
    const currentPrice = tickToPrice(currentTick);

    // Convert liquidity from string to Decimal
    const liquidityDecimal = new Decimal(liquidity);

    // Calculate sqrt prices for liquidity math
    const sqrtPriceLower = priceLower.sqrt();
    const sqrtPriceUpper = priceUpper.sqrt();
    const sqrtCurrentPrice = currentPrice.sqrt();

    let amount0: Decimal;
    let amount1: Decimal;

    if (currentPrice.lt(priceLower)) {
      // Position is entirely token0 (price below range)
      amount0 = liquidityDecimal
        .mul(sqrtPriceUpper.sub(sqrtPriceLower))
        .div(sqrtPriceUpper.mul(sqrtPriceLower));
      amount1 = new Decimal(0);
    } else if (currentPrice.gt(priceUpper)) {
      // Position is entirely token1 (price above range)
      amount0 = new Decimal(0);
      amount1 = liquidityDecimal.mul(sqrtPriceUpper.sub(sqrtPriceLower));
    } else {
      // Price is within range - calculate both amounts
      amount0 = liquidityDecimal
        .mul(sqrtPriceUpper.sub(sqrtCurrentPrice))
        .div(sqrtPriceUpper.mul(sqrtCurrentPrice));
      
      amount1 = liquidityDecimal.mul(sqrtCurrentPrice.sub(sqrtPriceLower));
    }

    // Convert amounts to human-readable format (accounting for decimals)
    const decimalsAdjustment0 = new Decimal(10).pow(token0Decimals);
    const decimalsAdjustment1 = new Decimal(10).pow(token1Decimals);
    
    const amount0Human = amount0.div(decimalsAdjustment0);
    const amount1Human = amount1.div(decimalsAdjustment1);

    // Calculate USD values
    const value0Usd = amount0Human.mul(token0PriceUsd);
    const value1Usd = amount1Human.mul(token1PriceUsd);
    const totalValueUsd = value0Usd.add(value1Usd);

    return {
      valueUsd: toApiNumber(totalValueUsd),
      amount0: toApiNumber(amount0Human),
      amount1: toApiNumber(amount1Human)
    };
  } catch (error) {
    console.error('Error calculating NFT position value:', error);
    return {
      valueUsd: 0,
      amount0: 0,
      amount1: 0
    };
  }
}